/**
 * Typed tool definition + grouped registration for WebMCP.
 *
 *   const group = new ToolGroup('editor', [defineTool({...}), ...]).register();
 *   group.dispose(); // unregisters all -> native `toolchange` fires
 *
 * The execute wrapper adds: recursive schema validation (structured error, never
 * throws), bounded JSON-serializable results (~1500 chars, `truncated: true`),
 * and a ledger entry per call with an optional undo closure recorded via
 * `ctx.recordUndo(fn, label)`.
 */
import { getModelContext, hasWebMCP, type ToolAnnotations, type NativeToolDescriptor } from './context';
import { ledger, type LedgerSource, type UndoFn } from './ledger';

export const RESULT_CHAR_LIMIT = 1500;

export interface JSONSchema {
  type?: 'object' | 'string' | 'number' | 'integer' | 'boolean' | 'array' | 'null';
  description?: string;
  properties?: Record<string, JSONSchema>;
  required?: string[];
  items?: JSONSchema;
  enum?: unknown[];
  additionalProperties?: boolean;
  [k: string]: unknown;
}

export interface ToolContext {
  signal?: AbortSignal;
  source: LedgerSource;
  /** Register a closure that reverts this call's side effects. Shown as an Undo button. */
  recordUndo(fn: UndoFn, label?: string): void;
  /** Override the one-line ledger summary (default: derived from result). */
  summarize(text: string): void;
}

export interface ToolDef<TInput = Record<string, unknown>> {
  name: string;
  description: string;
  inputSchema: JSONSchema;
  annotations?: ToolAnnotations;
  execute(input: TInput, ctx: ToolContext): unknown | Promise<unknown>;
}

export interface ToolError {
  error: { code: 'invalid_input' | 'execution_failed' | 'aborted' | 'unknown_tool'; message: string; issues?: string[] };
}

/** Identity helper that pins TInput for the execute signature. */
export function defineTool<TInput = Record<string, unknown>>(def: ToolDef<TInput>): ToolDef<TInput> {
  if (def.name.length > 30) console.warn(`[webmcp] tool name "${def.name}" exceeds 30 chars`);
  if (def.description.length > 500) console.warn(`[webmcp] "${def.name}" description exceeds 500 chars`);
  return def;
}

// ---------- validation (recursive: types, enums, bounds, required, additionalProperties) ----------

function typeOf(v: unknown): string {
  if (v === null) return 'null';
  if (Array.isArray(v)) return 'array';
  return typeof v;
}

/** Child path: `notes[0]`, `notes[0].bar`, `bars[1]`. */
function childPath(parent: string, key: string | number): string {
  if (typeof key === 'number') return `${parent}[${key}]`;
  return parent ? `${parent}.${key}` : key;
}

function validateValue(v: unknown, schema: JSONSchema, path: string, issues: string[]): void {
  const t = typeOf(v);
  const want = schema.type;
  if (want) {
    const ok =
      want === t ||
      (want === 'integer' && t === 'number' && Number.isInteger(v)) ||
      (want === 'number' && t === 'number');
    if (!ok) {
      issues.push(`"${path}" should be ${want}, got ${t}`);
      return;
    }
  }
  if (schema.enum && !schema.enum.includes(v)) issues.push(`"${path}" must be one of ${JSON.stringify(schema.enum)}`);
  if (t === 'number') {
    const n = v as number;
    if (typeof schema.minimum === 'number' && n < schema.minimum) issues.push(`${path} ${n} is below minimum ${schema.minimum}`);
    if (typeof schema.maximum === 'number' && n > schema.maximum) issues.push(`${path} ${n} exceeds maximum ${schema.maximum}`);
  } else if (t === 'string') {
    const str = v as string;
    if (typeof schema.minLength === 'number' && str.length < schema.minLength) {
      issues.push(`${path} is ${str.length} chars, below minLength ${schema.minLength}`);
    }
    if (typeof schema.maxLength === 'number' && str.length > schema.maxLength) {
      issues.push(`${path} is ${str.length} chars, exceeds maxLength ${schema.maxLength}`);
    }
    if (typeof schema.pattern === 'string') {
      let re: RegExp | null = null;
      try {
        re = new RegExp(schema.pattern, 'u');
      } catch {
        re = null;
      }
      if (re && !re.test(str)) issues.push(`${path} "${str}" does not match pattern ${schema.pattern}`);
    }
  } else if (t === 'array') {
    const arr = v as unknown[];
    if (typeof schema.minItems === 'number' && arr.length < schema.minItems) {
      issues.push(`${path} has ${arr.length} items, below minItems ${schema.minItems}`);
    }
    if (typeof schema.maxItems === 'number' && arr.length > schema.maxItems) {
      issues.push(`${path} has ${arr.length} items, exceeds maxItems ${schema.maxItems}`);
    }
    if (schema.items) arr.forEach((item, i) => validateValue(item, schema.items!, childPath(path, i), issues));
  } else if (t === 'object') {
    validateObject(v as Record<string, unknown>, schema, path, issues);
  }
}

function validateObject(obj: Record<string, unknown>, schema: JSONSchema, path: string, issues: string[]): void {
  for (const key of schema.required ?? []) {
    if (!(key in obj) || obj[key] === undefined) issues.push(`missing required "${childPath(path, key)}"`);
  }
  for (const [key, prop] of Object.entries(schema.properties ?? {})) {
    if (!(key in obj) || obj[key] === undefined) continue;
    validateValue(obj[key], prop, childPath(path, key), issues);
  }
  if (schema.additionalProperties === false) {
    for (const key of Object.keys(obj)) {
      if (!(key in (schema.properties ?? {}))) issues.push(`unexpected key "${childPath(path, key)}"`);
    }
  }
}

/**
 * Validate `input` against `schema` recursively: types, `enum`, `minimum` /
 * `maximum`, `minLength` / `maxLength` / `pattern`, `minItems` / `maxItems`,
 * `required` and `additionalProperties:false` at every level. Returns one
 * issue string per problem, e.g. `notes[0].bar 9 exceeds maximum 4`.
 */
export function validateInput(input: unknown, schema: JSONSchema): string[] {
  const issues: string[] = [];
  if (!schema || schema.type !== 'object') return issues;
  if (typeOf(input) !== 'object') return [`input must be an object, got ${typeOf(input)}`];
  validateObject(input as Record<string, unknown>, schema, '', issues);
  return issues;
}

// ---------- result bounding ----------

function toSerializable(v: unknown): unknown {
  if (v === undefined) return null;
  try {
    return JSON.parse(JSON.stringify(v));
  } catch {
    return String(v);
  }
}

export function boundResult(raw: unknown, limit = RESULT_CHAR_LIMIT): { value: unknown; truncated: boolean } {
  const value = toSerializable(raw);
  const text = typeof value === 'string' ? value : JSON.stringify(value);
  if (text.length <= limit) return { value, truncated: false };
  return {
    value: { truncated: true, totalChars: text.length, preview: text.slice(0, limit) + '…' },
    truncated: true,
  };
}

function summarizeResult(v: unknown): string {
  if (v && typeof v === 'object' && 'error' in (v as object)) return `error: ${(v as ToolError).error.message}`;
  const s = typeof v === 'string' ? v : JSON.stringify(v);
  return s.length > 80 ? s.slice(0, 77) + '…' : s;
}

// ---------- local registry (mirrors what we told the browser) ----------

export interface RegisteredTool {
  def: ToolDef<any>;
  group: string;
  /** Wrapped execute; safe to call from the inspector. */
  invoke(input: unknown, source?: LedgerSource, signal?: AbortSignal): Promise<unknown>;
}

const local = new Map<string, RegisteredTool>();
const registryListeners = new Set<() => void>();

function notifyRegistry() {
  for (const l of registryListeners) l();
}

export const registry = {
  list(): RegisteredTool[] {
    return [...local.values()];
  },
  get(name: string): RegisteredTool | undefined {
    return local.get(name);
  },
  count(): number {
    return local.size;
  },
  /** Fires on our own register/dispose; mirrors native `toolchange` for browsers without it. */
  subscribe(l: () => void): () => void {
    registryListeners.add(l);
    return () => registryListeners.delete(l);
  },
  async invoke(name: string, input: unknown, source: LedgerSource = 'inspector'): Promise<unknown> {
    const t = local.get(name);
    if (!t) return { error: { code: 'unknown_tool', message: `no tool named "${name}"` } } satisfies ToolError;
    return t.invoke(input, source);
  },
};

// ---------- wrapping ----------

function wrap<TInput>(def: ToolDef<TInput>, group: string): RegisteredTool {
  const invoke = async (input: unknown, source: LedgerSource = 'agent', signal?: AbortSignal): Promise<unknown> => {
    const started = performance.now();
    const issues = validateInput(input, def.inputSchema);
    if (issues.length) {
      const err: ToolError = { error: { code: 'invalid_input', message: 'Input failed validation', issues } };
      ledger.append({ tool: def.name, input, summary: `rejected: ${issues[0]}`, error: err.error, source });
      return err;
    }

    let undo: UndoFn | undefined;
    let undoLabel: string | undefined;
    let summary: string | undefined;
    const ctx: ToolContext = {
      signal,
      source,
      recordUndo(fn, label) {
        undo = fn;
        undoLabel = label;
      },
      summarize(text) {
        summary = text;
      },
    };

    try {
      const raw = await def.execute(input as TInput, ctx);
      const { value, truncated } = boundResult(raw);
      ledger.append({
        tool: def.name,
        input,
        summary: summary ?? summarizeResult(value) + (truncated ? ' (truncated)' : ''),
        result: value,
        durationMs: Math.round(performance.now() - started),
        source,
        undo,
        undoLabel,
      });
      return value;
    } catch (e) {
      const aborted = (e as any)?.name === 'AbortError' || signal?.aborted;
      const err: ToolError = {
        error: { code: aborted ? 'aborted' : 'execution_failed', message: (e as Error)?.message ?? String(e) },
      };
      ledger.append({
        tool: def.name,
        input,
        summary: summary ?? `failed: ${err.error.message}`,
        error: err.error,
        durationMs: Math.round(performance.now() - started),
        source,
      });
      return err;
    }
  };
  return { def, group, invoke };
}

// ---------- ToolGroup ----------

export class ToolGroup {
  private ac = new AbortController();
  private registered = false;
  readonly tools: ToolDef<any>[];

  constructor(readonly name: string, tools: ToolDef<any>[]) {
    this.tools = tools;
  }

  get disposed(): boolean {
    return this.ac.signal.aborted;
  }

  /** Registers every tool (locally always; natively when WebMCP is present). Idempotent. */
  register(): this {
    if (this.registered || this.disposed) return this;
    this.registered = true;
    const mc = getModelContext();
    for (const def of this.tools) {
      if (local.has(def.name)) console.warn(`[webmcp] tool "${def.name}" already registered; replacing`);
      const wrapped = wrap(def, this.name);
      local.set(def.name, wrapped);
      if (hasWebMCP() && mc?.registerTool) {
        const native: NativeToolDescriptor = {
          name: def.name,
          description: def.description,
          inputSchema: def.inputSchema as Record<string, unknown>,
          annotations: def.annotations,
          execute: (input, opts) => wrapped.invoke(input, 'agent', opts?.signal),
        };
        try {
          mc.registerTool(native, { signal: this.ac.signal });
        } catch (e) {
          console.error(`[webmcp] registerTool(${def.name}) failed`, e);
        }
      }
    }
    notifyRegistry();
    return this;
  }

  /** Unregisters everything in this group (abort signal + explicit unregisterTool fallback). */
  dispose(): void {
    if (this.disposed) return;
    this.ac.abort();
    const mc = getModelContext();
    for (const def of this.tools) {
      if (local.get(def.name)?.group === this.name) local.delete(def.name);
      if (mc?.unregisterTool) {
        try {
          mc.unregisterTool(def.name);
        } catch {
          /* already gone via signal */
        }
      }
    }
    notifyRegistry();
  }
}
